// Export: progress while every frame is drawn, then the finished video to watch and download.
import { useEffect, useState } from "react";
import { Body, Footer, Layer, Page, Question, TopBar } from "../common";
import { DEFAULT_EXPORT_SIZE, EXPORT_SIZES, exportSizeById, type ExportSize } from "../../model/exportSize";
import { CANT_ENCODE, exportEpisode } from "../exporter";
import type { Episode } from "../../model/episode";
import type { TimelineEpisode } from "../../model/timeline";

// The export flow for any episode (old or timeline), so the timeline editor can use it without the old loader.
export function ExportScreen({ ep, onClose }: { ep: Episode | TimelineEpisode; onClose: () => void }) {
  const [size, setSize] = useState<ExportSize | null>(null);
  const [pick, setPick] = useState<ExportSize>(savedSize);
  if (!size) {
    return (
      <Layer>
        <Page>
          <TopBar onBack={onClose} back="close" title="Export" sub={ep.title} />
          <Body>
            <Question hint="Sharper takes longer to make.">How sharp should the video be?</Question>
            <div className="segmented">
              {EXPORT_SIZES.map(s => (
                <button key={s.id} className={s.id === pick.id ? "on" : ""} onClick={() => setPick(s)}>{s.name} · {s.hint}</button>
              ))}
            </div>
          </Body>
          <Footer><button className="btn big yellow" onClick={() => { saveSize(pick); setSize(pick); }}>Make the video</button></Footer>
        </Page>
      </Layer>
    );
  }
  return <ExportRun ep={ep} size={size} onClose={onClose} />;
}

const KEY = "export-size";
function savedSize() {
  try { return exportSizeById(localStorage.getItem(KEY)) ?? DEFAULT_EXPORT_SIZE; } catch { return DEFAULT_EXPORT_SIZE; }
}
function saveSize(s: ExportSize) {
  try { localStorage.setItem(KEY, s.id); } catch {}
}

function ExportRun({ ep, size, onClose }: { ep: Episode | TimelineEpisode; size: ExportSize; onClose: () => void }) {
  const [p, setP] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const signal = { cancelled: false };
    exportEpisode(ep, size, setP, signal).then(u => !signal.cancelled && setUrl(u), e => !signal.cancelled && setErr((e as Error).message));
    return () => { signal.cancelled = true; };
  }, []);
  return (
    <Layer>
      <Page>
        <TopBar onBack={onClose} back="close" title="Export" sub={ep.title} />
        <Body>
          {url ? (
            <>
              <Question>Your {size.name} video is ready</Question>
              <video className="export-video" src={url} controls playsInline />
            </>
          ) : err ? (
            <>
              <Question>{err === CANT_ENCODE ? "Videos can't be made in this browser" : "The export didn't work"}</Question>
              <p className="error-text">{err}</p>
            </>
          ) : (
            <>
              <Question hint="Drawing every frame, then putting the sound on. Keep this screen open.">Making your video…</Question>
              <div className="progress light"><span style={{ width: `${p * 100}%` }} /></div>
              <p className="hint">{Math.round(p * 100)}%</p>
            </>
          )}
        </Body>
        {url && <Footer><a className="btn big yellow" href={url} download={`${ep.title || "sketchy"}.mp4`}>Download the video</a></Footer>}
      </Page>
    </Layer>
  );
}
